import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { PremiumPackagesState } from '@/domain/entities/premiumPackage/premiumPackagesState';
import type { UserPremiumPackageEntity } from '@/domain/entities/userPremiumPackage/userPremiumPackageEntity';
import type { RootState } from '@/presentation/store';
import { InAppPurchaseStatus } from '@/domain/enums/premiumPackages/InAppPurchaseStatus';
import { PremiumPackageType } from '@/domain/enums/premiumPackages/PremiumPackageType';
import { PremiumPackageActions } from '@/domain/enums/premiumPackages/PremiumPackageActions';
import type { IdentityVerificationStatus } from '@/domain/enums/premiumPackages/IdentityVerificationStatus';

const initialState: PremiumPackagesState = {
  userPremiumPackages: [],
  isLoaded: false,
};

const premiumPackagesSlice = createSlice({
  name: 'premiumPackages',
  initialState,
  reducers: {
    setUserPremiumPackages: (state, action: PayloadAction<UserPremiumPackageEntity[]>) => {
      state.userPremiumPackages = action.payload;
      state.isLoaded = true;
    },
    clearUserPremiumPackages: (state) => {
      state.userPremiumPackages = [];
      state.isLoaded = false;
    },
  },
});

export const { setUserPremiumPackages, clearUserPremiumPackages } = premiumPackagesSlice.actions;
export default premiumPackagesSlice.reducer;

// --- Selectors (replicate Flutter's PremiumPackagesNotifier methods) ---

export const selectUserPremiumPackages = (state: RootState): UserPremiumPackageEntity[] =>
  state.premiumPackages.userPremiumPackages;

export const selectPremiumPackagesLoaded = (state: RootState): boolean =>
  state.premiumPackages.isLoaded;

export const selectHasPremiumPackage = (state: RootState, productId: string): boolean =>
  state.premiumPackages.userPremiumPackages.some(
    (pkg) => pkg.productId === productId && pkg.active
  );

export const selectHasIdentityPackage = (state: RootState): boolean =>
  state.premiumPackages.userPremiumPackages.some(
    (pkg) => pkg.productId === PremiumPackageType.identityPro && pkg.active
  );

export const selectHasPremiumPackageEvenIfProcessing = (state: RootState, productId: string): boolean =>
  state.premiumPackages.userPremiumPackages.some(
    (pkg) =>
      pkg.productId === productId &&
      (pkg.active || pkg.status === InAppPurchaseStatus.pending)
  );

export const selectGetPremiumPackageStatus = (state: RootState, productId: string): InAppPurchaseStatus => {
  const pkg = state.premiumPackages.userPremiumPackages.find(
    (p) => p.productId === productId
  );
  if (!pkg) return InAppPurchaseStatus.none;
  return pkg.status as InAppPurchaseStatus;
};

export const selectGetIdentityVerificationStatus = (state: RootState): IdentityVerificationStatus | null => {
  const pkg = state.premiumPackages.userPremiumPackages.find(
    (p) => p.productId === PremiumPackageType.identityPro
  );
  if (!pkg || pkg.verificationStatus === undefined || pkg.verificationStatus === null) {
    return null;
  }
  return pkg.verificationStatus as IdentityVerificationStatus;
};

export const selectHasPremiumAction = (state: RootState, action: PremiumPackageActions): boolean =>
  state.premiumPackages.userPremiumPackages.some(
    (pkg) => pkg.active && pkg.actions.includes(action)
  );

export const selectHas4kAccess = (state: RootState): boolean =>
  selectHasPremiumAction(state, PremiumPackageActions.access4k);
